// Sidebar.js
import React from 'react';
import { useTheme, IconButton, Box, Button, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import MessageIcon from '@mui/icons-material/Message';
import CreateIcon from '@mui/icons-material/Create';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsightsIcon from '@mui/icons-material/Insights';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CloseIcon from '@mui/icons-material/Close';

const Sidebar = ({ open, onClose, headerHeight, collapsed, setCollapsed, variant }) => {
    const theme = useTheme();
    const isSmallScreen = variant === 'temporary';
    const isCollapsed = !isSmallScreen && collapsed;
    const location = useLocation();
    const currentPath = location.pathname + location.search;

    const sidebarItems = [
        { path: '/', text: 'Home', icon: HomeIcon },
        { path: '/pchannels', text: 'Add Channels', icon: AddIcon },
        { path: '/inbox', text: 'Inbox', icon: MessageIcon },
        { path: '/pchannels?action=createPost', text: 'Create Posts', icon: CreateIcon },
        { path: '/schedule', text: 'Schedule and Organize', icon: ScheduleIcon },
        { path: '/forminvite', text: 'Invite', icon: InsertInvitationIcon },
        { path: '/pendingposts', text: 'Approve Pending Posts', icon: CheckCircleIcon },
        { path: '/pinsights', text: 'Insights', icon: InsightsIcon },
        { path: '/pricing', text: 'Subscribe to a Plan', icon: LoyaltyIcon },
        { path: '/myaccount', text: 'My Account', icon: ManageAccountsIcon },
    ];

    // Floating margins (top/bottom/left) with header awareness for permanent drawer
    const floatingGapPx = parseInt(theme.spacing(2), 10) || 16; // safe parse
    const topOffsetPx = (variant === 'permanent' ? headerHeight : 0) + floatingGapPx;
    const floatingHeight = `calc(100% - ${topOffsetPx}px - ${floatingGapPx}px)`;

    return (
        <Drawer
            variant={variant}
            open={open}
            onClose={onClose}
            ModalProps={{
                keepMounted: true,
                BackdropProps: {
                    sx: {
                        backgroundColor: alpha(theme.palette.common.black, 0.45),
                        backdropFilter: 'blur(6px)',
                    },
                },
            }}
            PaperProps={{
                sx: {
                    overflowX: 'hidden',
                    borderRight: 'none',
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.background.paper, 0.88),
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 18px 50px rgba(0,0,0,0.20)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    ...(variant === 'permanent' && {
                        position: 'absolute',
                        top: floatingGapPx,
                        left: floatingGapPx,
                        height: `calc(100% - ${floatingGapPx * 2}px)`
                    }),
                    ...(variant === 'temporary' && {
                        top: floatingGapPx,
                        bottom: floatingGapPx,
                        left: floatingGapPx,
                        height: 'auto',
                        width: collapsed ? 72 : 'min(88vw, 340px)',
                        borderRadius: 4,
                        overflow: 'hidden',
                    }),
                },
            }}
            sx={{
                '& .MuiDrawer-paper': {
                    width: isCollapsed ? 60 : 240,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    px: collapsed ? 1 : 1.5,
                    py: 1.5,
                }}
            >
                {isSmallScreen && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1,
                            pb: 1,
                        }}
                    >
                        {!collapsed && (
                            <Box sx={{ fontWeight: 700, fontSize: 14 }}>
                                Menu
                            </Box>
                        )}

                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                ml: 'auto',
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                                border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
                                '&:hover': { backgroundColor: alpha(theme.palette.background.paper, 0.85) },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}

                {!isSmallScreen && (
                    <Button
                        onClick={() => setCollapsed(!collapsed)}
                        startIcon={collapsed ? <MenuIcon /> : <MenuOpenIcon />}
                        sx={{
                            position: 'absolute',
                            top: theme.spacing(2),
                            right: theme.spacing(2),
                            zIndex: 2,

                            textTransform: 'none',
                            borderRadius: collapsed? '100%': 2,
                            minWidth: collapsed ? 44 : 0,
                            height: 44,
                            px: collapsed ? 0 : 10,
                            width: collapsed ? 44 : 'calc(100% - 16px)',
                            justifyContent: isCollapsed ? 'center' : 'flex-start',
                            alignItems: isCollapsed ? 'center' : 'flex-start',

                            backgroundColor: alpha(theme.palette.background.paper, 0.85),
                            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                            boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
                            ...(collapsed && {
                                '& .MuiButton-startIcon': {
                                    margin: 0,
                                },
                            }),

                            '&:hover': {
                                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                            },
                        }}
                    >
                        {!collapsed && (
                            <Box component="span" sx={{ ml: 1, fontWeight: 600, fontSize: 14 }}>
                                Collapse menu
                            </Box>
                        )}
                    </Button>
                )}
                
                <Box
                    sx={{
                        flexGrow: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: collapsed ? 'center' : 'stretch',
                        gap: 1,
                        pt: isSmallScreen ? theme.spacing(1) : theme.spacing(6),
                        pb: theme.spacing(2),
                        overflowY: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    {sidebarItems.map((item) => {
                        const IconComponent = item.icon;
                        const isSelected = currentPath === item.path;

                        return (
                            <Button
                                key={item.text}
                                component={RouterLink}
                                to={item.path}
                                onClick={() => {
                                    if (isSmallScreen) onClose();
                                }}
                                sx={{
                                    mx: collapsed ? 0 : 1,
                                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                                    alignItems: "flex-start",
                                    textTransform: 'none',
                                    borderRadius: collapsed ? '100%' : 2,
                                    minWidth: 0,

                                    width: isCollapsed ? 44 : 'auto',
                                    height: 44,
                                    px: collapsed ? 0 : 1.5,

                                    color: isSelected
                                        ? theme.palette.primary.contrastText
                                        : theme.palette.text.primary,
                                    backgroundColor: isSelected
                                        ? theme.palette.primary.main
                                        : alpha(theme.palette.action.hover, 0.0),

                                    border: `1px solid ${isSelected ? alpha(theme.palette.primary.main, 0.35) : 'transparent'
                                        }`,
                                    boxShadow: isSelected ? '0 10px 26px rgba(0,0,0,0.18)' : 'none',

                                    '&:hover': {
                                        backgroundColor: isSelected
                                            ? theme.palette.primary.dark
                                            : alpha(theme.palette.action.hover, 0.1),
                                    },

                                    transition: theme.transitions.create(
                                        ['background-color', 'box-shadow', 'transform'],
                                        { duration: theme.transitions.duration.short }
                                    ),
                                    '&:active': { transform: 'translateY(1px)' },
                                    ...(isSmallScreen && {
                                        width: '100%',
                                        mx: 0,
                                        justifyContent: 'flex-start',
                                        px: 2,
                                        borderRadius: 3,
                                    }),

                                }}
                            >
                                <IconComponent
                                    sx={{
                                        fontSize: 24,
                                        color: isSelected
                                            ? theme.palette.primary.contrastText
                                            : theme.palette.text.primary,
                                    }}
                                />
                                {!isCollapsed && (
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 2,
                                            fontWeight: 600,
                                            fontSize: 14,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {item.text}
                                    </Box>
                                )}
                            </Button>
                        );
                    })}
                </Box>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
